import { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileXls, DownloadSimple, UploadSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { downloadBlob } from './helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UploadDialog = ({ open, onOpenChange, onUploaded }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await axios.get(`${API}/lineups/template.xlsx`, { responseType: 'blob' });
      downloadBlob(data, 'TulsiJobs_Lineup_Template.xlsx');
    } catch { toast.error('Could not download template'); }
  };

  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Choose an .xlsx file first'); return; }
    const fd = new FormData();
    fd.append('file', file);
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/lineups/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      toast.success(`${data.inserted} candidate${data.inserted === 1 ? '' : 's'} added · ${data.duplicates_skipped} duplicate${data.duplicates_skipped === 1 ? '' : 's'} skipped`);
      onUploaded(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setResult(null); }}>
      <DialogContent className="bg-white border-2 border-[#0A0A0A] shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] sm:max-w-[560px]" data-testid="upload-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-[#0A0A0A] flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            <FileXls size={28} weight="duotone" className="text-[#16A34A]" /> Upload Morning Excel
          </DialogTitle>
          <DialogDescription className="text-xs text-[#404040]">
            Columns: Date · Candidate Name · Mobile Number · Role / Job Category · Company Name · Lineup By · Telecaller Name.
            Candidates are auto-grouped by company and duplicates (same mobile, same day) are skipped.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={upload} className="space-y-3">
          <input ref={fileRef} data-testid="upload-file-input" type="file" accept=".xlsx,.xlsm" className="block w-full text-sm text-black font-medium border-2 border-dashed border-black rounded-md p-3 bg-[#F7FEE7]" />
          <div className="flex gap-2">
            <Button type="button" onClick={downloadTemplate} data-testid="download-template-btn" className="bg-[#93C5FD] text-black border-2 border-black rounded-md font-bold shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 transition-all">
              <DownloadSimple size={18} weight="bold" className="mr-1" /> Template
            </Button>
            <Button type="submit" disabled={busy} data-testid="upload-submit-btn" className="flex-1 bg-[#A3E635] text-black border-2 border-black rounded-md font-bold shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 transition-all">
              <UploadSimple size={18} weight="bold" className="mr-1" /> {busy ? 'Uploading…' : 'Upload & Segregate'}
            </Button>
          </div>
        </form>
        {result && (
          <div className="mt-2 space-y-2 text-sm text-black" data-testid="upload-result">
            <div className="flex gap-2 font-black">
              <span className="px-2 py-1 bg-[#4ADE80] border-2 border-black rounded">{result.inserted} added</span>
              <span className="px-2 py-1 bg-[#FDE047] border-2 border-black rounded">{result.duplicates_skipped} duplicates skipped</span>
              {result.errors?.length > 0 && <span className="px-2 py-1 bg-[#F87171] text-white border-2 border-black rounded">{result.errors.length} rows with errors</span>}
            </div>
            {result.companies?.length > 0 && <div className="text-xs font-medium">Companies: {result.companies.join(', ')}</div>}
            {result.errors?.length > 0 && (
              <ul className="text-xs text-[#B91C1C] font-medium max-h-28 overflow-y-auto list-disc pl-4">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
